import React from 'react';

/**
 * The one field class for the whole site. Imported, never copied — copied
 * class strings drift within two passes.
 *
 * 16px minimum (`text-base`), because below that iOS zooms the viewport on
 * focus and will not zoom back. `border-control` holds 3:1 against both the
 * white and cream surfaces.
 */
export const FIELD_CLASS = 'w-full rounded-control border border-control bg-white px-4 py-2.5 text-base text-ink placeholder:text-ink-muted focus:outline-none focus:border-moss focus:ring-2 focus:ring-moss/40 aria-[invalid=true]:border-rust aria-[invalid=true]:ring-rust/30';
interface FieldProps {
  id: string;
  label: string;
  /** Required is marked in text, never by colour or a bare asterisk. */
  required?: boolean;
  hint?: string;
  error?: string;
  type?: 'text' | 'email' | 'tel' | 'number';
  autoComplete?: string;
  inputMode?: 'text' | 'email' | 'tel' | 'numeric';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Renders a textarea instead of an input. */
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  className?: string;
}
export function Field({
  id,
  label,
  required = false,
  hint,
  error,
  type = 'text',
  autoComplete,
  inputMode,
  value,
  onChange,
  placeholder,
  multiline = false,
  rows = 3,
  maxLength,
  className = ''
}: FieldProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const shared = {
    id,
    name: id,
    value,
    required,
    autoComplete,
    placeholder,
    maxLength,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
    className: FIELD_CLASS
  };
  return <div data-component-id="C06" className={className} data-id="element-427" data-mp="138g0tmdkdu5o">
      <label htmlFor={id} className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-ink-muted" data-id="element-428" data-mp="1ztgmzcwd4ix7">
        {label}
        {!required && <span className="ml-1.5 font-medium normal-case tracking-normal text-ink-muted" data-id="element-429" data-mp="3p90vkl58ykmq">
            (optional)
          </span>}
      </label>

      {/* Hint sits below the label and above the input. */}
      {hint && <p id={hintId} className="mb-1.5 text-sm text-ink-muted" data-id="element-430" data-mp="3fj7uwf6n7px9">
          {hint}
        </p>}

      {multiline ? <textarea {...shared} rows={rows} onChange={event => onChange(event.target.value)} className={`${FIELD_CLASS} resize-none`} data-id="element-431" data-mp="3kgrapjizoarb" /> : <input {...shared} type={type} inputMode={inputMode} onChange={event => onChange(event.target.value)} data-id="element-432" data-mp="3bg2tyh27nwaq" />}

      {/* Error carries an icon and text, never colour alone. */}
      {error && <p id={errorId} className="mt-1.5 flex items-start gap-1.5 text-sm font-medium text-rust" data-id="element-433" data-mp="uul5s6rj00iw">
          <span aria-hidden="true" className="mt-px font-bold" data-id="element-434" data-mp="zrtpwcw8iems">
            ▲
          </span>
          <span data-id="element-435" data-mp="2kb71wiym4h71">{error}</span>
        </p>}
    </div>;
}

/**
 * A hidden field a person never sees and a bot fills. Paired with rate
 * limiting in production — see docs/registry.md, I04.
 */
export function Honeypot() {
  return <div aria-hidden="true" className="absolute left-[-9999px] h-px w-px overflow-hidden" data-id="element-436" data-mp="2p9qj6629h4t2">
      <label htmlFor="company-website" data-id="element-437" data-mp="33155alrgtm4c">Company website</label>
      <input id="company-website" name="company-website" type="text" tabIndex={-1} autoComplete="off" data-id="element-438" data-mp="32zkcb0ksub8l" />
    </div>;
}